import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Form, useFormikContext } from 'formik';
import { useConfirmationModal } from '@shared/hooks/useConfirmationModal';
import { getClosedTab, getSelectActiveTabIndex, getTabs } from '@store/reducers/tabsSlice';
import { useTabs } from '@shared/hooks/useTabs';
import { AlertModal } from './modals/AlertModal';

interface IUnsavedFormData {
  children?: JSX.Element | JSX.Element[] | null;
  onSaveDraftData?: () => void;
  dirtyWithoutFormik?: boolean;
  isDirty?: boolean;
}

export const FormWithPrompt = ({ dirtyWithoutFormik = false, isDirty, children, onSaveDraftData }: IUnsavedFormData) => {
  const activeTab = useSelector(getSelectActiveTabIndex);
  const tabs = useSelector(getTabs);
  const tabRef = useRef(activeTab);
  const dirty  = (dirtyWithoutFormik && isDirty ) || useFormikContext()?.dirty;
  const closedTab = useSelector(getClosedTab);
  const { selectTab, openTab } = useTabs();

  const { isOpen, onCancel, onConfirm, openDialog } = useConfirmationModal();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search) {
      return dirty;
    }
    return false;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      (async () => {
        const result = await openDialog();

        if (result) {
          // Proceed and set the current tab to active
          blocker.proceed?.();
          onSaveDraftData?.();
          tabRef.current = activeTab;
        } else {
          if (!tabs[tabRef.current]) {
            openTab(closedTab);
          } else {
            selectTab(tabRef.current);
          }
          blocker.reset?.();
        }
      })();
    } else {
      tabRef.current = activeTab;
    }
  }, [blocker, tabs, tabRef.current, openDialog, openTab, activeTab, closedTab, selectTab]);
  
  return (
    <>
      {dirtyWithoutFormik ? (
        <>
          {isOpen && (
            <AlertModal
              open
              textSize="16px"
              title="Alert!"
              subtitle="There are unsaved changes, do you want to proceed?"
              cancelButtonText="Cancel"
              buttonText="Okay"
              marginTop={0}
              onConfirm={onConfirm}
              onClose={onCancel}
            />
          )}
        </>
      ) : (
        <Form>
          {isOpen && (
            <AlertModal
              open
              textSize="16px"
              title="Alert!"
              subtitle="There are unsaved changes, do you want to proceed?"
              cancelButtonText="Cancel"
              buttonText="Okay"
              marginTop={0}
              onConfirm={onConfirm}
              onClose={onCancel}
            />
          )}
          {children}
        </Form>
      )}
    </>
  );
};
